import React, { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconCornerDownRight, IconPlus } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Icon,
  TextField,
  TextFieldType,
  Typography,
} from '@cfa/react-components';

import Constants from 'constants/index';

import 'react-smartbanner/dist/main.css';

import AddIcon from './AddIcon';

import { saveCategoriesMutation } from '@/_generated/api-clients/content';

export interface Category {
  icon: string;
  id: string;
  name: {
    en: string;
    es: string;
  };
  subcategories?: [
    {
      enabled: boolean;
      name: {
        en: string;
        es: string;
      };
      subtitles: string[];
    },
  ];
}

interface AddCategoryProps {
  categoriesArrayLength?: number;
  categoryToEdit?: Category;
  translationRequired?: boolean;
  isRecommended?: boolean;
  isSubcategory?: boolean;
  onCancel?: () => void;
  onClose?: () => void;
  onSubcategoryAdd?: (category: Category) => void;
  onSubcategoryCancel?: () => void;
  selectedCountry?: string;
}

export const AddCategory: React.FC<AddCategoryProps> = ({
  categoriesArrayLength = 0,
  categoryToEdit = {
    icon: '',
    id: '',
    name: {
      en: '',
      es: '',
    },
  },
  translationRequired,
  isRecommended,
  isSubcategory,
  onSubcategoryAdd,
  onCancel,
  onClose,
  onSubcategoryCancel,
  selectedCountry,
}) => {
  const editCategories = useMutation(saveCategoriesMutation());
  const [category, setCategory] = useState({
    icon: categoryToEdit.icon,
    id: categoryToEdit.id,
    name: {
      en: categoryToEdit.name.en,
      es: categoryToEdit.name.es,
    },
    subcategories: [
      {
        name: {
          en: '',
          es: '',
        },
      },
    ],
  });
  const [showAddIconPopUp, setShowAddIconPopUp] = useState(false);
  const { t } = useTranslation();
  const firstSubcategoryName = category.subcategories[0]?.name;
  const firstSubcategoryNameEn = firstSubcategoryName?.en.trim() ?? '';
  const firstSubcategoryNameEs = firstSubcategoryName?.es.trim() ?? '';

  const onNameChange = (e: React.ChangeEvent<TextFieldType>) => {
    const name = e.target.value;
    if (name.length) {
      setCategory({
        ...category,
        name: { en: name, es: category.name.es },
      });
    } else {
      setCategory({
        ...category,
        name: {
          en: '',
          es: category.name.es,
        },
      });
    }
  };

  const onTranslationChange = (e: React.ChangeEvent<TextFieldType>) => {
    const translation = e.target.value;
    if (translation.length) {
      setCategory({
        ...category,
        name: { en: category.name.en, es: translation },
      });
    } else {
      setCategory({
        ...category,
        name: {
          en: category.name.en,
          es: '',
        },
      });
    }
  };

  const onSubcategoryNameChange = (e: React.ChangeEvent<TextFieldType>) => {
    const name = e.target.value;
    if (name.length) {
      setCategory({
        ...category,
        subcategories: [
          {
            name: {
              en: name,
              es: firstSubcategoryName?.es ?? '',
            },
          },
        ],
      });
    } else {
      setCategory({
        ...category,
        subcategories: [
          {
            name: {
              en: '',
              es: firstSubcategoryName?.es ?? '',
            },
          },
        ],
      });
    }
  };

  const onSubcategoryTranslationChange = (
    e: React.ChangeEvent<TextFieldType>,
  ) => {
    const translation = e.target.value;
    if (translation.length) {
      setCategory({
        ...category,
        subcategories: [
          {
            name: {
              en: firstSubcategoryName?.en ?? '',
              es: translation,
            },
          },
        ],
      });
    } else {
      setCategory({
        ...category,
        subcategories: [
          {
            name: {
              en: firstSubcategoryName?.en ?? '',
              es: '',
            },
          },
        ],
      });
    }
  };

  const onAddCategory = () => {
    const trimmedName = category.name.en.trim();
    const trimmedTranslation = category.name.es.trim();
    const payload = [
      {
        country: selectedCountry,
        displayIndex: categoriesArrayLength,
        enabled: false,
        featured: isRecommended ? true : false,
        icon: category.icon,
        name: {
          en: trimmedName,
          es: trimmedTranslation,
        },
      },
    ];
    editCategories
      .mutateAsync({ body: payload })
      .then(() => {
        onClose?.();
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  const onAddIcon = (icon: string) => {
    setCategory({ ...category, icon });
    setShowAddIconPopUp(false);
  };

  return (
    <div className="border-outline-3 shadow-elevation-5 relative mb-4 rounded-sm p-4">
      <div className="flex">
        {!isSubcategory && (
          <div className="mr-4 flex flex-col items-center">
            <Typography
              className="mb-2 text-nowrap"
              data-testid="AdminAddIconWrapper"
              fontWeight="medium"
              variant="body2"
            >
              {t('Admin.addIcon')}
              <span className="text-feedback-error-base">*</span>
            </Typography>
            {category.icon ? (
              <button
                type="button"
                aria-label={t('Admin.editIcon')}
                onClick={() => {
                  setShowAddIconPopUp(true);
                }}
              >
                <img
                  alt={category.icon}
                  className="size-10 cursor-pointer"
                  data-testid="Icon"
                  src={`${Constants.PATHWAY_CDN_IMG.CATEGORIES}${category.icon}.svg`}
                />
              </button>
            ) : (
              <Icon
                cursor="pointer"
                data-testid={'AddIcon'}
                icon={() => (
                  <IconPlus
                    className="size-10 cursor-pointer"
                    onClick={() => setShowAddIconPopUp(true)}
                  />
                )}
                size="md"
              />
            )}
          </div>
        )}
        <div className="flex grow flex-col">
          <TextField
            data-testid={'AddCategoryNameInput'}
            fullWidth
            label={
              isSubcategory
                ? t('Admin.subcategory')
                : isRecommended
                  ? t('Admin.recommendedCategory')
                  : t('Admin.category')
            }
            onChange={isSubcategory ? onSubcategoryNameChange : onNameChange}
            placeholder={'Enter Name'}
            required={true}
            value={
              isSubcategory
                ? category.subcategories.at(0)?.name.en
                : category.name.en
            }
          />

          <div className="mt-4 flex">
            <IconCornerDownRight size={35} />
            <div className="flex grow flex-col">
              <TextField
                data-testid={'AddCategoryTranslationInput'}
                fullWidth
                label={t('Admin.spanishTranslation')}
                onChange={
                  isSubcategory
                    ? onSubcategoryTranslationChange
                    : onTranslationChange
                }
                placeholder={'Enter Translation'}
                required={translationRequired}
                value={
                  isSubcategory
                    ? category.subcategories.at(0)?.name.es
                    : category.name.es
                }
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex">
        <div className="mt-4 flex">
          <Button
            className="mr-2"
            color="secondary"
            onClick={isSubcategory ? onSubcategoryCancel : onCancel}
            variant="outlined"
          >
            {t('Button.cancel')}
          </Button>
          <Button
            color="secondary"
            data-testid="AddButton"
            disabled={
              isSubcategory && !translationRequired
                ? firstSubcategoryNameEn.length <= 0
                : isSubcategory
                  ? firstSubcategoryNameEn.length <= 0 ||
                    firstSubcategoryNameEs.length <= 0
                  : !translationRequired
                    ? category.name.en.trim().length <= 0 ||
                      category.icon.trim().length <= 0
                    : category.name.en.trim().length <= 0 ||
                      category.name.es.trim().length <= 0 ||
                      category.icon.trim().length <= 0
            }
            onClick={
              isSubcategory
                ? () => {
                    onSubcategoryAdd?.({
                      ...category,
                      subcategories: [
                        {
                          enabled: false,
                          name: {
                            en: category.subcategories.at(0)?.name.en ?? '',
                            es: category.subcategories.at(0)?.name.es ?? '',
                          },
                          subtitles: [],
                        },
                      ],
                    });
                  }
                : onAddCategory
            }
          >
            {t('Button.add')}
          </Button>
        </div>
      </div>
      {showAddIconPopUp && (
        <AddIcon
          className="absolute top-[75px] left-[32px] z-5 translate-[unset]!"
          onAddIcon={icon => onAddIcon(icon)}
          onClose={() => setShowAddIconPopUp(false)}
        />
      )}
    </div>
  );
};

export default AddCategory;
